/**
 * Passar para outra pessoa — a conversa, ou a oportunidade.
 *
 * ── Um diálogo só, com dois alvos ─────────────────────────────────────────
 *
 * Porque é uma operação só. A rota da conversa move a oportunidade junto; a da
 * oportunidade move a conversa. O que muda entre elas é por onde se entra —
 * não o que acontece —, e dois diálogos divergiriam no primeiro texto novo,
 * com a mesma ação explicada de dois jeitos conforme a tela.
 *
 * Ele é uma feature própria pelo mesmo motivo de `entities/atendimento/etiqueta`: abre no
 * cabeçalho da conversa, no card do funil e na linha da lista de clientes.
 * Deixá-lo em `atendimento/` acoplaria três telas por um detalhe de interface,
 * e a primeira refatoração de uma quebraria as outras sem aviso.
 *
 * ── Por que a lista mostra quem NÃO pode receber ──────────────────────────
 *
 * Porque esconder responde "não existe" a quem pergunta "cadê o Fulano?", e a
 * resposta certa é "existe, e falta isto". Quem procura um colega e não o
 * encontra conclui que a lista está quebrada; quem o encontra desabilitado com
 * o motivo escrito sabe a quem pedir o quê.
 *
 * ── O motivo é obrigatório ────────────────────────────────────────────────
 *
 * Não é burocracia: "por que este cliente mudou de vendedor?" é uma pergunta
 * que aparece meses depois, quando ninguém lembra. Três palavras registradas na
 * hora valem mais que qualquer reconstrução.
 *
 * ── A confirmação diz o que MOVEU, item a item ────────────────────────────
 *
 * A operação mexe em dois sistemas, e não há transação entre eles. Um "pronto!"
 * genérico esconderia o caso em que a conversa foi e a oportunidade não — que é
 * o estado que ninguém percebe até o funil e o WhatsApp discordarem.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useResponsaveisDaOportunidade,
  useResponsaveisPossiveis,
} from '@/shared/api/consultas';
import { Botao, Campo, Chip, Dialogo } from '@/shared/ui';
import { nomeDoPapel } from '@/entities/acesso/papel';
import './atribuir.css';

/** O que se está passando de mão. */
export type AlvoDaAtribuicao =
  { tipo: 'conversa'; id: string } | { tipo: 'oportunidade'; id: string };

const EXPLICACAO_DO_MOTIVO: Record<string, string> = {
  sem_acesso_ao_atendimento: 'ainda não tem acesso ao atendimento',
  sem_cadastro_no_crm: 'está com o cadastro incompleto',
  vinculo_suspenso: 'está com o acesso suspenso',
};

/**
 * O que DESTRAVA cada motivo, e por que isso não cabe no rótulo da linha.
 *
 * O rótulo diz o obstáculo em três palavras, ao lado do nome, e é assim que
 * ele tem que ser: a lista tem uma linha por pessoa e precisa ser varrida com
 * o olho. Mas o obstáculo sozinho manda o vendedor pedir "acesso ao CRM" sem
 * saber o que isso quer dizer — e quem recebe o pedido também não sabe.
 *
 * Então o caminho aparece UMA vez, embaixo da lista, e só para os motivos que
 * de fato apareceram. Repetir por linha empilharia a mesma frase três vezes; e
 * mostrar sempre ensinaria a ignorar.
 */
const COMO_DESTRAVAR: Record<string, string> = {
  sem_acesso_ao_atendimento: 'Quem administra a organização dá acesso ao atendimento.',
  sem_cadastro_no_crm:
    'Quem administra a organização cadastra a pessoa no painel do CRM e depois liga o cadastro ' +
    'a ela — o CRM não deixa criar cadastro pela integração.',
  vinculo_suspenso: 'Quem administra a organização reativa o acesso em Configurações.',
};

/** O texto do alvo: o que a tela promete mover, e o que ela não promete. */
const PALAVRAS = {
  conversa: {
    titulo: 'Passar esta conversa',
    semDono: 'A conversa ficou sem dono.',
    exemplo: 'ex.: cliente da região do Paulo',
    soltar: 'volta para a fila',
  },
  oportunidade: {
    titulo: 'Passar esta oportunidade',
    semDono: 'A oportunidade ficou sem dono.',
    exemplo: 'ex.: o Paulo assumiu a região',
    soltar: 'fica sem responsável no funil',
  },
} as const;

export function Atribuir({
  alvo,
  responsavelAtual,
  aberto,
  aoFechar,
}: {
  alvo: AlvoDaAtribuicao;
  /** Quem responde hoje — só para oferecer "deixar sem responsável". */
  responsavelAtual: string | null;
  aberto: boolean;
  aoFechar: () => void;
}): ReactNode {
  // Montar só quando abre: o diálogo busca a equipe nas duas engines, e
  // ninguém precisa disso por abrir uma conversa ou um funil.
  if (!aberto) return null;
  return (
    <DialogoDeAtribuicao alvo={alvo} responsavelAtual={responsavelAtual} aoFechar={aoFechar} />
  );
}

function DialogoDeAtribuicao({
  alvo,
  responsavelAtual,
  aoFechar,
}: {
  alvo: AlvoDaAtribuicao;
  responsavelAtual: string | null;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [escolhido, definirEscolhido] = useState<string | null>(null);
  const [motivo, definirMotivo] = useState('');
  const palavras = PALAVRAS[alvo.tipo];

  /*
   * Os dois ganchos são chamados SEMPRE, e só um fica ligado.
   *
   * Chamar condicionalmente quebraria a ordem dos hooks entre renders — e o
   * React não perdoa isso. O `ligado` é o que decide qual dos dois vai à rede.
   */
  const daConversa = useResponsaveisPossiveis(alvo.id, alvo.tipo === 'conversa');
  const daOportunidade = useResponsaveisDaOportunidade(alvo.id, alvo.tipo === 'oportunidade');
  const { data, isPending, isError } = alvo.tipo === 'conversa' ? daConversa : daOportunidade;

  /*
   * A chave nasce com o DIÁLOGO, e não com o clique.
   *
   * É o que faz o duplo clique — e o retry depois de um erro de rede — cair na
   * mesma chave, em vez de registrar duas passagens de mão do mesmo fato.
   */
  const [chaveDaTentativa] = useState(() => crypto.randomUUID());

  const passar = useMutation({
    mutationFn: async () => {
      if (!api) throw new Error('Sem sessão.');
      const corpo = { usuario_id: escolhido, motivo: motivo.trim() };

      if (alvo.tipo === 'conversa') {
        const feito = await api.transferirConversa({
          caminho: { id: alvo.id },
          corpo,
          idempotencyKey: chaveDaTentativa,
        });
        return {
          responsavel: feito.responsavel,
          linhas: [
            feito.moveu.conversa ? '✓ A conversa mudou de dono.' : '— A conversa não mudou.',
            feito.moveu.oportunidade
              ? '✓ A oportunidade foi junto.'
              : '— Não havia oportunidade para levar junto.',
          ],
        };
      }

      const feito = await api.atribuirOportunidade({ caminho: { id: alvo.id }, corpo });
      return {
        responsavel: feito.responsavel,
        linhas: [
          feito.moveu.oportunidade
            ? '✓ A oportunidade mudou de dono.'
            : '— A oportunidade não mudou.',
          /*
           * O número, e não um "sim". Uma oportunidade pode ter mais de uma
           * conversa ligada, e "as conversas foram" esconderia que só três das
           * quatro andaram.
           */
          feito.moveu.conversas === 0
            ? '— Não havia conversa ligada para levar junto.'
            : feito.moveu.conversas === 1
              ? '✓ A conversa ligada foi junto.'
              : `✓ ${feito.moveu.conversas} conversas ligadas foram junto.`,
        ],
      };
    },
    onSuccess: () => {
      /*
       * As três consultas, e não só a do alvo.
       *
       * A operação mexe nos dois lados: passar pelo funil muda a fila, e passar
       * pela conversa muda o card. Invalidar só o que estava na tela deixaria a
       * outra mostrando o dono antigo até alguém recarregar.
       */
      void cache.invalidateQueries({ queryKey: chaves.inbox() });
      void cache.invalidateQueries({ queryKey: chaves.funil() });
      if (alvo.tipo === 'conversa') {
        void cache.invalidateQueries({ queryKey: chaves.conversa(alvo.id) });
      }
    },
  });

  const podeEnviar = motivo.trim().length >= 3 && !passar.isPending;

  if (passar.isSuccess) {
    const { responsavel, linhas } = passar.data;
    return (
      <Dialogo
        titulo={responsavel ? `Agora é do ${responsavel.nome}.` : palavras.semDono}
        aoFechar={aoFechar}
        botoes={
          <Botao variante="primario" onClick={aoFechar}>
            Fechar
          </Botao>
        }
      >
        <ul className="transferencia__resultado">
          {linhas.map((linha) => (
            <li key={linha}>{linha}</li>
          ))}
          {/*
            O cliente aparece com o porquê, e não some da lista. Sumindo,
            alguém suporia que foi junto — e ele continua aparecendo para todo
            mundo, porque cliente não tem dono neste produto (decisão de
            24/08). Dizer isso é mais barato que deixar descobrir depois.
          */}
          <li className="transferencia__nota">
            O cliente não muda de dono: neste produto o cliente é da organização.
          </li>
        </ul>
      </Dialogo>
    );
  }

  return (
    <Dialogo
      titulo={palavras.titulo}
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            desabilitado={!podeEnviar}
            motivo="Escolha para quem passar, e escreva o porquê."
            onClick={() => passar.mutate()}
          >
            {passar.isPending ? 'Passando…' : 'Passar'}
          </Botao>
        </>
      }
    >
      {isPending && <p>Buscando quem pode receber…</p>}
      {isError && <p className="dialogo__erro">Não foi possível buscar a lista. Tente de novo.</p>}

      {data && (
        <ul className="lista-de-pessoas">
          {data.itens.map((pessoa) => (
            <li key={pessoa.usuario_id}>
              <label className={pessoa.pode_receber ? '' : 'lista-de-pessoas--sem'}>
                <input
                  type="radio"
                  name="responsavel"
                  value={pessoa.usuario_id}
                  disabled={!pessoa.pode_receber}
                  checked={escolhido === pessoa.usuario_id}
                  onChange={() => definirEscolhido(pessoa.usuario_id)}
                />
                <span className="lista-de-pessoas__nome">{pessoa.nome}</span>
                <span className="lista-de-pessoas__papel">{nomeDoPapel(pessoa.papel)}</span>
                {pessoa.motivo && (
                  <span className="lista-de-pessoas__motivo">
                    {EXPLICACAO_DO_MOTIVO[pessoa.motivo] ?? pessoa.motivo}
                  </span>
                )}
                {pessoa.atual && <Chip>é quem responde hoje</Chip>}
              </label>
            </li>
          ))}
          <li>
            {/*
              Deixar sem dono é uma opção explícita, e não a ausência de
              escolha. Devolver à fila é uma decisão de verdade — e sem esta
              linha ela só seria possível por caminho nenhum.
            */}
            <label>
              <input
                type="radio"
                name="responsavel"
                checked={escolhido === null && responsavelAtual !== null}
                onChange={() => definirEscolhido(null)}
              />
              <span className="lista-de-pessoas__nome">Deixar sem responsável</span>
              <span className="lista-de-pessoas__motivo">{palavras.soltar}</span>
            </label>
          </li>
        </ul>
      )}

      {data && <ComoDestravar itens={data.itens} />}

      <Campo
        rotulo="Por que está passando?"
        type="text"
        value={motivo}
        maxLength={280}
        onChange={(e) => definirMotivo(e.currentTarget.value)}
        placeholder={palavras.exemplo}
      />

      {passar.isError && (
        <p className="dialogo__erro">
          {passar.error instanceof Error
            ? passar.error.message
            : 'Não foi possível passar. Nada foi alterado.'}
        </p>
      )}
    </Dialogo>
  );
}

/**
 * O caminho para destravar quem não pode receber — uma linha por motivo que
 * apareceu na lista, e nada quando ninguém está travado.
 *
 * A ordem é a de `COMO_DESTRAVAR`, e não a de quem apareceu primeiro na lista:
 * a mesma tela mostrando as mesmas frases em ordens diferentes conforme o
 * alfabeto da equipe faria quem lê reler tudo toda vez.
 */
function ComoDestravar({ itens }: { itens: readonly { motivo: string | null }[] }): ReactNode {
  const motivos = new Set(itens.map((i) => i.motivo).filter((m): m is string => m !== null));
  const linhas = Object.entries(COMO_DESTRAVAR).filter(([motivo]) => motivos.has(motivo));

  if (linhas.length === 0) return null;

  return (
    <ul className="transferencia__notas" aria-label="Como liberar quem não pode receber">
      {linhas.map(([motivo, comoDestravar]) => (
        <li key={motivo} className="transferencia__nota">
          <strong>{EXPLICACAO_DO_MOTIVO[motivo]}</strong> — {comoDestravar}
        </li>
      ))}
    </ul>
  );
}
